import { avoidTollRoadFlags, avoidTollRoadFlagsEnum } from "helpers/enums/avoidTollRoadFlags";

type AvoidTollRoadFlag = (typeof avoidTollRoadFlags)[number];
type AvoidTollRoadFlagValue = (typeof avoidTollRoadFlagsEnum)[keyof typeof avoidTollRoadFlagsEnum];
type AvoidTollRoadFlagWithDisabled<T> = T & { disabled?: boolean };

const flagIntValue = (value: AvoidTollRoadFlagValue): number =>
    avoidTollRoadFlags.find((item) => item.value === value)?.intValue ?? 0;

const m12Flag = flagIntValue(avoidTollRoadFlagsEnum.M12);
const m12DrtFlag = flagIntValue(avoidTollRoadFlagsEnum.M12DRT);
const m12NchFlag = flagIntValue(avoidTollRoadFlagsEnum.M12NCH);
const m12WithoutDrtFlag = flagIntValue(avoidTollRoadFlagsEnum.M12WithoutDRT);
const m12PartFlagsMask = m12DrtFlag | m12NchFlag | m12WithoutDrtFlag;

export const hasFlag = (value: number | null | undefined, flag: number | AvoidTollRoadFlagValue): boolean => {
    const intValue = typeof flag === "number" ? flag : flagIntValue(flag);
    return intValue !== 0 && ((value ?? 0) & intValue) === intValue;
};

export const normalizeAvoidTollRoadFlags = (value?: number | null): number => {
    const flags = value ?? 0;
    const hasM12 = hasFlag(flags, m12Flag);
    const hasM12Drt = hasFlag(flags, m12DrtFlag);
    const hasM12Nch = hasFlag(flags, m12NchFlag);
    const hasM12WithoutDrt = hasFlag(flags, m12WithoutDrtFlag);

    if (hasM12 || (hasM12WithoutDrt && hasM12Drt)) {
        return (flags & ~m12PartFlagsMask) | m12Flag;
    }

    if (hasM12WithoutDrt && hasM12Nch) {
        return flags & ~m12NchFlag;
    }

    return flags;
};

export const withDisabledAvoidTollRoadFlags = <T extends Pick<AvoidTollRoadFlag, "value" | "intValue">>(
    flags: T[],
    value?: number | null,
): AvoidTollRoadFlagWithDisabled<T>[] => {
    const normalizedValue = normalizeAvoidTollRoadFlags(value);
    const hasM12 = hasFlag(normalizedValue, m12Flag);
    const hasM12Drt = hasFlag(normalizedValue, m12DrtFlag);
    const hasM12Nch = hasFlag(normalizedValue, m12NchFlag);
    const hasM12WithoutDrt = hasFlag(normalizedValue, m12WithoutDrtFlag);

    return flags.map((item) => {
        const disabled =
            (hasM12 &&
                [avoidTollRoadFlagsEnum.M12DRT, avoidTollRoadFlagsEnum.M12NCH, avoidTollRoadFlagsEnum.M12WithoutDRT]
                    .includes(item.value)) ||
            (hasM12WithoutDrt &&
                [avoidTollRoadFlagsEnum.M12, avoidTollRoadFlagsEnum.M12DRT, avoidTollRoadFlagsEnum.M12NCH].includes(
                    item.value,
                )) ||
            ((hasM12Drt || hasM12Nch) &&
                [avoidTollRoadFlagsEnum.M12, avoidTollRoadFlagsEnum.M12WithoutDRT].includes(item.value));

        return disabled ? { ...item, disabled } : item;
    });
};
